import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Typography, makeStyles, List, ListItem, ListItemText } from '@material-ui/core';
import { Content, MarkdownContent, Page } from '@backstage/core-components';
import { useApi, discoveryApiRef } from '@backstage/core-plugin-api';
import { useNavigate } from 'react-router-dom';

interface TocEntry {
  level: number;
  text: string;
  id: string;
}

const EMOJI_RE = /[\u{1F300}-\u{1F9FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{FE00}-\u{FE0F}\u{200D}]/gu;

function stripEmoji(s: string): string {
  return s.replace(EMOJI_RE, '').trim();
}

function toSlug(s: string): string {
  return s
    .toLowerCase()
    .replace(EMOJI_RE, '')
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function parseToc(md: string): TocEntry[] {
  const entries: TocEntry[] = [];
  for (const line of md.split('\n')) {
    const match = line.match(/^(#{1,3})\s+(.+)$/);
    if (match) {
      const raw = match[2].replace(/[*_`]/g, '');
      const text = stripEmoji(raw);
      const id = toSlug(raw);
      entries.push({ level: match[1].length, text, id });
    }
  }
  return entries;
}

const NAV_WIDTH = 260;

const useStyles = makeStyles(theme => ({
  wrapper: {
    display: 'flex',
    gap: theme.spacing(3),
  },
  nav: {
    width: NAV_WIDTH,
    minWidth: NAV_WIDTH,
    position: 'sticky',
    top: theme.spacing(2),
    alignSelf: 'flex-start',
    maxHeight: 'calc(100vh - 120px)',
    overflowY: 'auto',
    borderRight: `1px solid ${theme.palette.divider}`,
    paddingRight: theme.spacing(1),
  },
  navItem: {
    padding: theme.spacing(0.5, 1),
    borderRadius: 4,
    cursor: 'pointer',
    '&:hover': {
      background: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
    },
  },
  navItemActive: {
    background: theme.palette.type === 'dark' ? 'rgba(144,202,249,0.15)' : 'rgba(25,118,210,0.08)',
    borderLeft: `3px solid ${theme.palette.primary.main}`,
  },
  navText: {
    '& span': {
      fontSize: '0.8125rem',
      lineHeight: 1.4,
    },
  },
  content: {
    flex: 1,
    minWidth: 0,
    maxWidth: 900,
    '& h1': {
      fontSize: '2rem',
      fontWeight: 400,
      borderBottom: `1px solid ${theme.palette.divider}`,
      paddingBottom: theme.spacing(1),
      marginTop: theme.spacing(4),
      marginBottom: theme.spacing(2),
    },
    '& h2': {
      fontSize: '1.5rem',
      fontWeight: 400,
      borderBottom: `1px solid ${theme.palette.divider}`,
      paddingBottom: theme.spacing(0.5),
      marginTop: theme.spacing(3),
      marginBottom: theme.spacing(1.5),
    },
    '& h3': {
      fontSize: '1.25rem',
      fontWeight: 500,
      marginTop: theme.spacing(2.5),
      marginBottom: theme.spacing(1),
    },
    '& p': {
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: 1.7,
      marginBottom: theme.spacing(1.5),
    },
    '& blockquote': {
      borderLeft: `4px solid ${theme.palette.primary.main}`,
      margin: theme.spacing(2, 0),
      padding: theme.spacing(1, 2),
      background: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
      '& p': { margin: 0 },
    },
    '& ul, & ol': {
      paddingLeft: theme.spacing(3),
      marginBottom: theme.spacing(1.5),
      '& li': { lineHeight: 1.7, marginBottom: theme.spacing(0.5) },
    },
    '& hr': {
      border: 'none',
      borderTop: `1px solid ${theme.palette.divider}`,
      margin: theme.spacing(3, 0),
    },
    '& strong': {
      fontWeight: 600,
    },
    '& code': {
      background: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
      padding: '2px 6px',
      borderRadius: 3,
      fontSize: '0.875em',
    },
  },
}));

export const TechRadarLegendPage = () => {
  const classes = useStyles();
  const discoveryApi = useApi(discoveryApiRef);
  const navigate = useNavigate();
  const [markdown, setMarkdown] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const [activeId, setActiveId] = useState<string>('');
  const contentRef = useRef<HTMLDivElement>(null);

  const toc = useMemo(() => (markdown ? parseToc(markdown) : []), [markdown]);

  // Inject id attributes onto rendered headings so scroll-to works
  const injectHeadingIds = useCallback(() => {
    const container = contentRef.current;
    if (!container || !toc.length) return;
    const headings = container.querySelectorAll('h1, h2, h3');
    const tocCopy = [...toc];
    headings.forEach(el => {
      const slug = toSlug(el.textContent || '');
      const idx = tocCopy.findIndex(t => t.id === slug);
      if (idx !== -1) {
        el.setAttribute('id', slug);
        tocCopy.splice(idx, 1);
      }
    });
  }, [toc]);

  useEffect(() => {
    discoveryApi.getBaseUrl('tech-radar').then(base =>
      fetch(`${base}/guide`, { cache: 'no-store' })
        .then(r => r.text())
        .then(setMarkdown)
        .catch(() => setError(true)),
    );
  }, [discoveryApi]);

  // Inject IDs and observe heading visibility for active nav highlighting
  useEffect(() => {
    if (!toc.length) return;
    // Small delay to let MarkdownContent render
    const timer = setTimeout(() => {
      injectHeadingIds();
      const observer = new IntersectionObserver(
        entries => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              setActiveId(entry.target.id);
              break;
            }
          }
        },
        { rootMargin: '-80px 0px -60% 0px', threshold: 0.1 },
      );
      for (const item of toc) {
        const el = document.getElementById(item.id);
        if (el) observer.observe(el);
      }
      return () => observer.disconnect();
    }, 300);
    return () => clearTimeout(timer);
  }, [toc, injectHeadingIds]);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveId(id);
    }
  };

  return (
    <Page themeId="documentation">
      <Content>
        <Box mb={2}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => navigate('/tech-radar')}
            style={{ color: '#90caf9', borderColor: '#90caf9' }}
          >
            ← Back to radar
          </Button>
        </Box>
        {!markdown && !error && (
          <Box display="flex" justifyContent="center" mt={6}>
            <CircularProgress />
          </Box>
        )}
        {error && (
          <Typography style={{ color: '#ef9a9a' }}>Failed to load guide.</Typography>
        )}
        {markdown && (
          <div className={classes.wrapper}>
            <nav className={classes.nav}>
              <Typography variant="overline" style={{ padding: '8px 8px 4px', display: 'block', fontWeight: 600 }}>
                Contents
              </Typography>
              <List dense disablePadding>
                {toc.map(entry => (
                  <ListItem
                    key={entry.id}
                    className={`${classes.navItem} ${activeId === entry.id ? classes.navItemActive : ''}`}
                    onClick={() => scrollTo(entry.id)}
                    style={{ paddingLeft: entry.level === 1 ? 8 : entry.level === 2 ? 20 : 32 }}
                    button
                  >
                    <ListItemText
                      className={classes.navText}
                      primary={entry.text}
                    />
                  </ListItem>
                ))}
              </List>
            </nav>
            <div className={classes.content} ref={contentRef}>
              <MarkdownContent content={markdown} />
            </div>
          </div>
        )}
      </Content>
    </Page>
  );
};

